import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileArchive, ArrowRight, CheckCircle2, Download } from "lucide-react";
import { trackClick } from "@/lib/trackClick";
import { useUser } from "@/contexts/UserContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { canAccessFullTool } from "@/utils/accessControl";

interface DocumentPackBannerProps {
  variant?: "inline" | "sidebar" | "full-width";
  trackingId?: string;
}

export function DocumentPackBanner({ variant = "inline", trackingId = "doc_pack_banner" }: DocumentPackBannerProps) {
  const { currentUser } = useUser();
  const { language } = useLanguage();
  const hasWordPack = currentUser.hasWordPack;
  const hasPro = canAccessFullTool(currentUser.plan);

  const downloadSecure = async () => {
    const { secureDownload, getDocPackKey } = await import('@/lib/secureDownload');
    secureDownload(getDocPackKey(language));
  };

  // If user already owns the pack or Pro, don't show purchase banners
  if (hasWordPack || hasPro) return null;

  if (variant === "sidebar") {
    return (
      <div className="rounded-xl border border-accent/30 bg-accent/5 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <FileArchive className="h-5 w-5 text-accent" />
          <h4 className="font-bold text-sm text-foreground">ISO 27001 Document Pack</h4>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          40+ editable Word documents plus 2 Excel registers (Risk Register & Statement of Applicability). No watermarks. Yours forever for $99.
        </p>
        <ul className="space-y-1.5">
          {["40+ Word policies & procedures", "2 Excel registers (Risk Register & SoA)", "No watermarks, edit freely"].map((item) => (
            <li key={item} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3 w-3 text-accent flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <Button
          asChild
          size="sm"
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground gap-1"
        >
          <Link to="/iso-27001-templates" onClick={() => trackClick(`${trackingId}_sidebar`, "Get Document Pack - $99")}>
            Get Document Pack - $99 <ArrowRight className="w-3 h-3" />
          </Link>
        </Button>
      </div>
    );
  }

  if (variant === "full-width") {
    return (
      <section className="py-12 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent border-y border-accent/20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-5xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                <FileArchive className="h-7 w-7 text-accent" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">
                  Get 40+ Word Documents + 2 Excel Registers
                </h3>
                <p className="text-muted-foreground text-sm max-w-lg">
                  Stop copying from PDFs. Get clean, professional Word files you can customize with your company name and logo, plus 2 Excel registers (Risk Register and Statement of Applicability). One-time purchase, yours forever.
                </p>
              </div>
            </div>
            <Button
              asChild
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2 px-8 shadow-lg whitespace-nowrap"
            >
              <Link to="/iso-27001-templates" onClick={() => trackClick(`${trackingId}_fullwidth`, "Get Document Pack - $99")}>
                Get Document Pack - $99 <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Default "inline" variant
  return (
    <div className="max-w-4xl mx-auto bg-gradient-to-r from-accent/15 to-accent/5 border border-accent/30 rounded-2xl p-8 sm:p-10">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="h-16 w-16 rounded-2xl bg-accent/20 flex items-center justify-center flex-shrink-0">
          <FileArchive className="h-8 w-8 text-accent" />
        </div>
        <div className="text-center md:text-left flex-1">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
            Skip the Copy-Paste. Get Editable Word & Excel Files.
          </h3>
          <p className="text-white/60 text-sm leading-relaxed mb-1">
            40+ professionally written ISO 27001 policies and templates in clean Word format, plus 2 Excel registers (Risk Register and Statement of Applicability). No watermarks. One purchase, yours forever.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 justify-center md:justify-start">
            {["40+ Word documents", "2 Excel registers (Risk Register & SoA)", "No watermarks", "$99 one-time"].map((item) => (
              <span key={item} className="flex items-center gap-1 text-xs text-accent font-medium">
                <CheckCircle2 className="h-3 w-3" />
                {item}
              </span>
            ))}
          </div>
        </div>
        <Button
          asChild
          size="lg"
          className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2 px-8 shadow-lg whitespace-nowrap"
        >
          <Link to="/iso-27001-templates" onClick={() => trackClick(`${trackingId}_inline`, "Get Document Pack - $99")}>
            Get Document Pack - $99 <ArrowRight className="w-5 h-5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
